import { useApi, useLoad } from "@allergy123/api-client/react";
import { FILL_SOURCES, type PanelPosition, type RuleIssue } from "@allergy123/contracts/allergens";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { CatalogHeader, errorText, publishIssues } from "./common";

interface Edit {
  position: number;
  antigenId: string;
  trayLabel: string;
  fillSource: (typeof FILL_SOURCES)[number];
  mixRecipeId: string | null;
}

interface Block {
  start: number;
  block: string;
  arm: PanelPosition["arm"];
  wells: (PanelPosition | { position: number; empty: true })[];
}

/** Keyed by id, so moving between versions (for example to a new draft) never shows the last one's grid, selection or check results. */
export function PanelVersionPage() {
  const { id = "" } = useParams();
  return <PanelVersionEditor key={id} id={id} />;
}

function PanelVersionEditor({ id }: { id: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const data = useLoad((a) => a.admin.catalog.panelVersion(id));
  const antigens = useLoad((a) => a.admin.catalog.antigens());
  const recipes = useLoad((a) => a.admin.catalog.recipes());
  const families = useLoad((a) => a.admin.catalog.panels());
  const [selected, setSelected] = useState<number | null>(null);
  const [edit, setEdit] = useState<Edit | null>(null);
  const [issues, setIssues] = useState<RuleIssue[] | null>(null);
  const [note, setNote] = useState("");
  const [compareTo, setCompareTo] = useState("");
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const v = data.data;
  const isDraft = v?.status === "draft";
  const byPosition = useMemo(() => new Map((v?.positions ?? []).map((p) => [p.position, p])), [v]);
  // Every message for a well is kept: a well can break more than one rule.
  const issueAt = useMemo(() => {
    const out = new Map<number, string[]>();
    for (const i of issues ?? []) if (i.position !== undefined) out.set(i.position, [...(out.get(i.position) ?? []), i.message]);
    return out;
  }, [issues]);
  const sortedIssues = useMemo(() => [...(issues ?? [])].sort((a, b) => (a.position ?? 1000) - (b.position ?? 1000)), [issues]);

  useEffect(() => {
    if (selected === null) {
      setEdit(null);
      return;
    }
    const p = byPosition.get(selected);
    setEdit({ position: selected, antigenId: p?.antigenId ?? "", trayLabel: p?.trayLabel ?? "", fillSource: p?.fillSource ?? "product", mixRecipeId: p?.mixRecipeId ?? null });
  }, [selected, byPosition]);

  // Below the xl breakpoint the editor sits under the grid, so bring it into view when a different well is chosen.
  const editingPosition = edit?.position;
  useEffect(() => {
    if (editingPosition !== undefined) editorRef.current?.scrollIntoView({ block: "nearest" });
  }, [editingPosition]);

  async function saveWell() {
    if (busy || !edit) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.savePanelPositions(id, [{ ...edit, trayLabel: edit.trayLabel.trim() }]);
      setMsg({ kind: "info", text: `Saved position ${edit.position}.${issues ? " Check again to see what is left." : ""}` });
      setIssues(null);
      data.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function check() {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.admin.catalog.checkPanel(id);
      setIssues(res.issues);
      setMsg(res.issues.length ? { kind: "error", text: `${res.issues.length} issue(s) to fix before publishing.` } : { kind: "info", text: "Ready to publish." });
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    if (busy || !v) return;
    setBusy(true);
    setMsg(null);
    try {
      let summary = "";
      if (v.basedOnVersionId) {
        const cmp = await api.admin.catalog.comparePanels(v.basedOnVersionId, id).catch(() => null);
        summary = cmp ? `${cmp.differences.length} well(s) differ from the version this draft started from.` : "";
      }
      if (!window.confirm(`Publish ${v.family.code} version ${v.version}? It becomes the current tray for every location on this panel. ${summary}`)) return;
      await api.admin.catalog.publishPanel(id, note.trim() || undefined);
      setMsg({ kind: "info", text: `Published ${v.family.code} version ${v.version}.` });
      setIssues(null);
      data.reload();
    } catch (err) {
      // A refusal for broken rules lists them, as the check does.
      const refused = publishIssues(err);
      if (refused) setIssues(refused);
      setMsg({ kind: "error", text: refused ? `${refused.length} issue(s) to fix before publishing.` : errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function newDraft() {
    if (busy || !v) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.admin.catalog.createPanelDraft(v.family.id, v.id);
      navigate(`/catalog/panels/versions/${res.draft.id}`);
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const blocks = useMemo(() => {
    const out: Block[] = [];
    for (let start = 1; start <= 60; start += 5) {
      const wells = Array.from({ length: 5 }, (_, i) => byPosition.get(start + i) ?? { position: start + i, empty: true as const });
      // The block's name and arm come from any filled well in it, so an empty first well doesn't rename the block.
      const known = wells.find((w): w is PanelPosition => !("empty" in w));
      out.push({ start, block: known?.block ?? `Wells ${start}–${start + 4}`, arm: known?.arm ?? (start <= 30 ? "right" : "left"), wells });
    }
    return out;
  }, [byPosition]);

  const antigenOptions = [{ value: "", label: "Choose an antigen" }, ...(antigens.data?.antigens ?? []).map((a) => ({ value: a.id, label: `${a.displayName} (${a.abbreviation})` }))];
  // The recipe a well already uses stays in the list even once retired, so the select shows the truth; the check flags it.
  const recipeChoices = (recipes.data?.recipes ?? [])
    .filter((r) => r.mixAntigenId === edit?.antigenId && (r.status === "active" || r.id === edit?.mixRecipeId))
    .map((r) => ({ value: r.id, label: `${r.name} v${r.version}${r.status === "active" ? "" : ` (${r.status})`}` }));
  // Every published and draft version of every panel except this one, so any two versions or panels can be compared.
  const compareChoices = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    for (const f of families.data?.families ?? []) {
      if (f.published && f.published.id !== id) out.push({ value: f.published.id, label: `${f.code} v${f.published.version} (published)` });
      if (f.draft && f.draft.id !== id) out.push({ value: f.draft.id, label: `${f.code} v${f.draft.version} (draft)` });
    }
    return out;
  }, [families.data, id]);
  const selectedWell = selected === null ? undefined : byPosition.get(selected);

  return (
    <>
      <CatalogHeader title={v ? `${v.family.name} · v${v.version} (${v.status})` : "Tray panel"} intro={!v ? "One version of a tray panel and its 60 wells." : isDraft ? "Click a well to change it. Check the draft, then publish." : "Published and retired versions are read-only."} />
      {data.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the panel (${errorText(data.error)})`} /></div>}
      {antigens.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load antigens (${errorText(antigens.error)})`} /></div>}
      {recipes.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load recipes (${errorText(recipes.error)})`} /></div>}
      {families.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the panel list for comparing (${errorText(families.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      {v && (
        <div className="mb-6 flex flex-wrap items-end gap-3">
          {isDraft ? (
            <>
              <Button variant="secondary" disabled={busy} onClick={() => void check()}>Check</Button>
              <Field label="Publish note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="What changed and why" />
              <Button disabled={busy} onClick={() => void publish()}>Publish</Button>
            </>
          ) : (
            v.status === "published" && <Button variant="secondary" disabled={busy} onClick={() => void newDraft()}>Start a new draft from this version</Button>
          )}
          {v.basedOnVersionId && <Link to={`/catalog/panels/compare?a=${v.basedOnVersionId}&b=${v.id}`} className="self-end pb-2 text-anchor underline">Compare with the version it started from</Link>}
        </div>
      )}
      {v && (
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <Select
            label="Compare with…"
            value={compareTo}
            onChange={(e) => setCompareTo(e.target.value)}
            options={[{ value: "", label: families.state === "loading" ? "Loading…" : compareChoices.length ? "Choose a version" : "No other versions yet" }, ...compareChoices]}
          />
          <Button variant="secondary" disabled={!compareTo} onClick={() => navigate(`/catalog/panels/compare?a=${id}&b=${compareTo}`)}>Compare</Button>
        </div>
      )}
      {data.state === "loading" && <p className="text-ink-secondary">Loading…</p>}
      {issues && issues.length > 0 && (
        <Card as="section" dense className="mb-6" aria-label="Issues to fix">
          <h2 className="mb-2 text-body font-semibold text-anchor">Issues to fix ({issues.length})</h2>
          <ul className="grid gap-1 text-small">
            {sortedIssues.map((i, n) => (
              <li key={n}>
                {i.position !== undefined && (
                  <>
                    <button type="button" onClick={() => setSelected(i.position!)} className="font-medium text-anchor underline">Position {i.position}</button>
                    {": "}
                  </>
                )}
                {i.message}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {v && (
        <div className="grid gap-6 xl:grid-cols-[1fr_18rem]">
          <div className="grid gap-4 lg:grid-cols-2">
            {(["right", "left"] as const).map((arm) => (
              <section key={arm} aria-label={`${arm} arm`} className="grid min-w-0 content-start gap-3">
                <h2 className="text-h3 font-semibold text-anchor">{arm === "right" ? "Right arm" : "Left arm"}</h2>
                {blocks.filter((b) => b.arm === arm).map((b) => (
                  <Card key={b.start} dense className="p-3!">
                    <h3 className="mb-2 text-small font-semibold">{b.block}</h3>
                    <div className="grid grid-cols-5 gap-1.5">
                      {b.wells.map((w) => {
                        const p = "empty" in w ? null : w;
                        const problems = issueAt.get(w.position);
                        const isSelected = selected === w.position;
                        // The tray label is a patient-safety identifier, so it is never clipped: a long one wraps inside its well.
                        return (
                          <button
                            key={w.position}
                            type="button"
                            onClick={() => setSelected(w.position)}
                            aria-pressed={isSelected}
                            aria-label={p ? `Position ${w.position}, ${p.trayLabel}, ${p.displayName}${problems ? ", needs fixing" : ""}` : `Position ${w.position}, empty${problems ? ", needs fixing" : ""}`}
                            title={[p?.displayName ?? "Empty", ...(problems ?? [])].join("\n")}
                            className={`min-w-0 rounded-sm text-left text-small focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ${isSelected ? "border-2 border-selection-indicator bg-selection p-[5px]" : "border border-rule-strong bg-card p-1.5"}`}
                          >
                            <div className="text-ink-secondary">{w.position}</div>
                            <div className="break-all font-semibold leading-tight">{p?.trayLabel ?? "—"}</div>
                            {problems && <div className="mt-0.5 font-semibold text-status-error">Fix</div>}
                          </button>
                        );
                      })}
                    </div>
                  </Card>
                ))}
              </section>
            ))}
          </div>
          <div ref={editorRef} className="min-w-0 self-start xl:sticky xl:top-6">
            <Card as="section">
              {selected === null && <p className="text-ink-secondary">Select a well to see {isDraft ? "or change " : ""}its antigen.</p>}
              {selected !== null && !isDraft && (
                <p>
                  Position {selected}: {selectedWell ? `${selectedWell.displayName} (${selectedWell.fillSource === "mix_recipe" ? "in-house mix" : "purchased product"})` : "empty"}
                </p>
              )}
              {edit && isDraft && (
                <div className="grid gap-4">
                  <h2 className="text-h3 font-semibold text-anchor">Position {edit.position}</h2>
                  <Select label="Antigen" value={edit.antigenId} onChange={(e) => {
                    const a = antigens.data?.antigens.find((x) => x.id === e.target.value);
                    setEdit({ ...edit, antigenId: e.target.value, trayLabel: a?.abbreviation ?? edit.trayLabel, mixRecipeId: null });
                  }} options={antigenOptions} />
                  <Field label="Tray label" value={edit.trayLabel} onChange={(e) => setEdit({ ...edit, trayLabel: e.target.value.toUpperCase() })} maxLength={10} hint="At most 10 characters; unique within the panel." />
                  <Select label="Filled from" value={edit.fillSource} onChange={(e) => setEdit({ ...edit, fillSource: e.target.value as Edit["fillSource"], mixRecipeId: null })} options={[{ value: "product", label: "A purchased product" }, { value: "mix_recipe", label: "An in-house mix recipe" }]} />
                  {edit.fillSource === "mix_recipe" && edit.antigenId && recipes.state === "ready" && recipeChoices.length === 0 && (
                    <p className="text-small text-ink-secondary">
                      No active recipe for this antigen — create one under <Link to="/catalog/recipes" className="text-anchor underline">Mix recipes</Link>.
                    </p>
                  )}
                  {edit.fillSource === "mix_recipe" && recipeChoices.length > 0 && (
                    <Select label="Mix recipe" value={edit.mixRecipeId ?? ""} onChange={(e) => setEdit({ ...edit, mixRecipeId: e.target.value || null })} options={[{ value: "", label: "Choose a recipe" }, ...recipeChoices]} />
                  )}
                  {issueAt.get(edit.position)?.map((m, n) => <Status key={n} kind="error" label={m} />)}
                  <div><Button disabled={busy || !edit.antigenId || !edit.trayLabel.trim() || (edit.fillSource === "mix_recipe" && !edit.mixRecipeId)} onClick={() => void saveWell()}>Save position</Button></div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
